/**
 * UPT-2124 J4a / J4b (spec `web/SPEC-UPT-2124-J4.md` §2 n° 1, 3, 5, 6) — l'ACTEUR D'INTERFACE : Géode agit sur ce que l'écran expose à un lecteur
 * d'écran (rôle + nom accessible + région), jamais sur des sélecteurs CSS ; une page n'a rien à câbler pour être pilotable.
 *
 * - Périmètre (n° 5) : le contenu principal (`main`, `[role=main]`) et le tiroir / dialogue ouvert (`[role=dialog]`, `[role=alertdialog]`,
 *   `[data-voice-scope]`) ; JAMAIS l'en-tête du portail, le menu utilisateur, un `iframe`, un champ `password`, un champ `data-sensitive` ou masqué
 *   (valeurs `••••`), ni ce qui porte `data-voice-exclude`. Ce qui est caché (`hidden`, `aria-hidden`, `inert`, `display:none`) n'existe pas.
 * - Nom accessible (n° 6) : calculé comme un lecteur d'écran (`aria-labelledby`, `aria-label`, `<label for>` / étiquette englobante, contenu texte,
 *   `title`, `placeholder`) ; correspondance normalisée (minuscules, sans accents) ; AMBIGUÏTÉ = aucune action (la liste des candidats est rendue).
 * - Jeu natif des événements (n° 3) : `focus`, setter natif de `HTMLInputElement.prototype.value` (idem `select`, `textarea`) puis `input` / `change`
 *   qui bouillonnent (React voit la valeur), case à cocher par `click`.
 *
 * Aucune lecture réseau, aucun stockage. Pur autant que possible : le document est un paramètre (jsdom en test).
 */
import { normalizeUiText, wordsOf } from './uiRiskTable';

export type UiRole =
  | 'button'
  | 'link'
  | 'tab'
  | 'menuitem'
  | 'row'
  | 'option'
  | 'checkbox'
  | 'radio'
  | 'switch'
  | 'textbox'
  | 'spinbutton'
  | 'combobox'
  | 'listbox'
  | 'radiogroup'
  | 'heading'
  | 'region'
  | 'dialog'
  | 'group'
  | 'form'
  | 'tabpanel'
  | 'table'
  | 'list'
  | 'other';

/** Rôles qu'un « clique sur … » peut viser. */
export const CLICKABLE_ROLES: readonly UiRole[] = ['button', 'link', 'tab', 'menuitem', 'row', 'option', 'checkbox', 'radio', 'switch', 'other'];
/** Rôles qu'un « mets … dans … » peut remplir. */
export const FILLABLE_ROLES: readonly UiRole[] = ['textbox', 'spinbutton', 'combobox', 'checkbox', 'switch', 'radio', 'radiogroup', 'listbox'];
/** Rôles qui nomment une zone (ui_read {region}, ui_scroll {to: label}). */
export const REGION_ROLES: readonly UiRole[] = ['dialog', 'region', 'form', 'tabpanel', 'group', 'table', 'list', 'listbox', 'radiogroup'];

const EXPLICIT_ROLES: Readonly<Record<string, UiRole>> = {
  button: 'button',
  link: 'link',
  tab: 'tab',
  menuitem: 'menuitem',
  menuitemcheckbox: 'menuitem',
  menuitemradio: 'menuitem',
  row: 'row',
  option: 'option',
  checkbox: 'checkbox',
  radio: 'radio',
  switch: 'switch',
  textbox: 'textbox',
  searchbox: 'textbox',
  spinbutton: 'spinbutton',
  slider: 'spinbutton',
  combobox: 'combobox',
  listbox: 'listbox',
  radiogroup: 'radiogroup',
  heading: 'heading',
  region: 'region',
  dialog: 'dialog',
  alertdialog: 'dialog',
  group: 'group',
  form: 'form',
  tabpanel: 'tabpanel',
  table: 'table',
  grid: 'table',
  treegrid: 'table',
  list: 'list',
  treeitem: 'option',
};

const INLINE_TAGS = new Set(['span', 'b', 'strong', 'i', 'em', 'small', 'u', 'sub', 'sup', 'abbr', 'code', 'mark', 'bdi', 'bdo', 'a', 'label', 'time', 'q']);
const TEXT_INPUT_TYPES = new Set(['text', 'email', 'search', 'tel', 'url', 'date', 'time', 'datetime-local', 'month', 'week', 'password', '']);

const attr = (el: Element, name: string): string => el.getAttribute(name) ?? '';

/** Le rôle de l'élément : `role` explicite connu, sinon implicite de la balise ; `null` quand l'élément n'expose rien d'utile. */
export const roleOf = (el: Element): UiRole | null => {
  const explicit = attr(el, 'role').trim().split(/\s+/)[0]?.toLowerCase();
  if (explicit && EXPLICIT_ROLES[explicit]) {
    return EXPLICIT_ROLES[explicit];
  }
  const tag = el.tagName.toLowerCase();
  switch (tag) {
    case 'button':
    case 'summary':
      return 'button';
    case 'a':
      return el.hasAttribute('href') ? 'link' : null;
    case 'input': {
      const type = (attr(el, 'type') || 'text').toLowerCase();
      if (type === 'button' || type === 'submit' || type === 'reset' || type === 'image') return 'button';
      if (type === 'checkbox') return 'checkbox';
      if (type === 'radio') return 'radio';
      if (type === 'number' || type === 'range') return 'spinbutton';
      if (TEXT_INPUT_TYPES.has(type)) return 'textbox';
      return null;
    }
    case 'textarea':
      return 'textbox';
    case 'select': {
      const select = el as HTMLSelectElement;
      return select.multiple || select.size > 1 ? 'listbox' : 'combobox';
    }
    case 'tr':
      return 'row';
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      return 'heading';
    case 'dialog':
      return 'dialog';
    case 'form':
      return el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby') ? 'form' : null;
    case 'section':
      return el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby') ? 'region' : null;
    case 'fieldset':
      return 'group';
    case 'table':
      return 'table';
    case 'ul':
    case 'ol':
      return 'list';
    default:
      break;
  }
  if (attr(el, 'contenteditable').toLowerCase() === 'true') {
    return 'textbox';
  }
  if (el.hasAttribute('data-voice-clickable') || attr(el, 'tabindex') === '0') {
    return 'other';
  }
  return null;
};

const collapse = (text: string): string => text.replace(/\s+/g, ' ').trim();

const isHiddenElement = (el: Element, includeAriaHidden: boolean): boolean => {
  if (el.hasAttribute('hidden') || el.hasAttribute('inert')) return true;
  if (includeAriaHidden && attr(el, 'aria-hidden') === 'true') return true;
  const view = el.ownerDocument.defaultView;
  const style = view?.getComputedStyle(el);
  return style?.display === 'none';
};

/** Le texte d'un nœud tel qu'un lecteur d'écran le lit (icônes, éléments cachés et contrôles embarqués sauf `labelled` laissés de côté). */
const textOf = (node: Node, { includeHidden = false }: { includeHidden?: boolean } = {}): string => {
  if (node.nodeType === 3) {
    return node.textContent ?? '';
  }
  if (node.nodeType !== 1) {
    return '';
  }
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (!includeHidden && isHiddenElement(el, true)) return '';
  if (tag === 'script' || tag === 'style' || tag === 'svg' || tag === 'select' || tag === 'textarea' || tag === 'input') return '';
  if (tag === 'img') return attr(el, 'alt');
  if (tag === 'br') return ' ';
  const own = attr(el, 'aria-label');
  if (own && el.parentElement && tag !== 'button' && tag !== 'a') {
    // Un descendant nommé (badge, icône étiquetée) apporte son nom ; le contenu de ses propres enfants n'est pas relu.
    return ` ${own} `;
  }
  let text = '';
  el.childNodes.forEach((child) => {
    text += textOf(child, { includeHidden });
  });
  return INLINE_TAGS.has(tag) ? text : ` ${text} `;
};

const idsOf = (value: string): string[] => value.split(/\s+/).filter((id) => id.length > 0);

const labelsOf = (el: Element): HTMLLabelElement[] => {
  const control = el as HTMLInputElement;
  const fromApi = control.labels ? Array.from(control.labels) : [];
  if (fromApi.length > 0) return fromApi;
  const found: HTMLLabelElement[] = [];
  const id = attr(el, 'id');
  if (id) {
    el.ownerDocument.querySelectorAll('label[for]').forEach((label) => {
      if (label.getAttribute('for') === id) found.push(label as HTMLLabelElement);
    });
  }
  const wrapping = el.closest('label');
  if (wrapping && !found.includes(wrapping as HTMLLabelElement)) found.push(wrapping as HTMLLabelElement);
  return found;
};

const CONTENT_NAMED_ROLES = new Set<UiRole>(['button', 'link', 'tab', 'menuitem', 'option', 'checkbox', 'radio', 'switch', 'heading', 'row', 'other']);

/** Le nom accessible de l'élément, calculé comme un lecteur d'écran (texte brut, espaces réduits ; `''` quand il n'en a pas). */
export const accessibleName = (el: Element): string => {
  // Une ligne se nomme par ce qu'elle montre (AG Grid pose « Press SPACE to select this row » en aria-label : une aide, pas un nom).
  if (roleOf(el) === 'row' && !attr(el, 'aria-labelledby').trim()) {
    return collapse(textOf(el));
  }
  const labelledBy = attr(el, 'aria-labelledby').trim();
  if (labelledBy) {
    const doc = el.ownerDocument;
    const parts = idsOf(labelledBy)
      .map((id) => doc.getElementById(id))
      .filter((node): node is HTMLElement => node !== null)
      .map((node) => collapse(textOf(node, { includeHidden: true })));
    const joined = collapse(parts.join(' '));
    if (joined) return joined;
  }
  const ariaLabel = collapse(attr(el, 'aria-label'));
  if (ariaLabel) return ariaLabel;

  const tag = el.tagName.toLowerCase();
  if (tag === 'input') {
    const type = (attr(el, 'type') || 'text').toLowerCase();
    if (type === 'button' || type === 'submit' || type === 'reset') {
      const value = collapse(attr(el, 'value'));
      if (value) return value;
    }
    if (type === 'image') {
      const alt = collapse(attr(el, 'alt'));
      if (alt) return alt;
    }
  }
  if (tag === 'input' || tag === 'select' || tag === 'textarea') {
    const fromLabels = collapse(
      labelsOf(el)
        .map((label) => textOf(label))
        .join(' ')
    );
    if (fromLabels) return fromLabels;
  }
  if (tag === 'fieldset') {
    const legend = el.querySelector(':scope > legend');
    if (legend) return collapse(textOf(legend));
  }
  const role = roleOf(el);
  if (role && CONTENT_NAMED_ROLES.has(role)) {
    const content = collapse(textOf(el));
    if (content) return content;
  }
  const title = collapse(attr(el, 'title'));
  if (title) return title;
  if (tag === 'input' || tag === 'textarea') {
    const placeholder = collapse(attr(el, 'placeholder'));
    if (placeholder) return placeholder;
  }
  return '';
};

/** Marque d'un affichage masqué (valeurs tokenisées, mots de passe) : jamais lu, jamais écrit. */
const MASK_PATTERN = /[•●∗]{2,}|\*{4,}/;

export const looksMasked = (text: string): boolean => MASK_PATTERN.test(text);

/** Un champ que Géode ne lit ni n'écrit : `password`, `data-sensitive` (lui ou un ancêtre), jetons de saisie, valeur ou affichage masqués. */
export const isSensitive = (el: Element): boolean => {
  if (el.closest('[data-sensitive]')) return true;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input') {
    const input = el as HTMLInputElement;
    const type = (attr(el, 'type') || 'text').toLowerCase();
    if (type === 'password') return true;
    const autocomplete = attr(el, 'autocomplete').toLowerCase();
    if (autocomplete.startsWith('cc-') || autocomplete === 'one-time-code' || autocomplete.endsWith('-password')) return true;
    if (looksMasked(input.value)) return true;
  }
  if (tag === 'textarea' && looksMasked((el as HTMLTextAreaElement).value)) return true;
  if (tag !== 'input' && tag !== 'textarea' && tag !== 'select' && looksMasked(el.textContent ?? '')) return true;
  return false;
};

/** Cache de visibilité d'une passe : un élément est visible quand ni lui ni un ancêtre n'est caché. */
type VisibilityCache = WeakMap<Element, boolean>;

const isRendered = (el: Element, cache: VisibilityCache): boolean => {
  const known = cache.get(el);
  if (known !== undefined) return known;
  let visible = !isHiddenElement(el, true);
  if (visible) {
    const view = el.ownerDocument.defaultView;
    const style = view?.getComputedStyle(el);
    if (style?.visibility === 'hidden') visible = false;
  }
  if (visible && el.parentElement && el.parentElement !== el.ownerDocument.documentElement) {
    visible = isRendered(el.parentElement, cache);
  }
  cache.set(el, visible);
  return visible;
};

/**
 * Hors périmètre : ce qui porte `data-voice-exclude` (en-tête du portail, panneau vocal, bouton micro, tiroir du copilote), les `iframe` (bac à sable des
 * prototypes), l'en-tête et le pied de page, le dock vocal du mode Géode et les cartes de confirmation / « prochaine étape » : Géode ne peut ni arrêter sa
 * propre session ni répondre « oui » à sa propre question.
 */
const EXCLUDED_SELECTOR =
  '[data-voice-exclude], iframe, [role="banner"], footer, [role="contentinfo"], [data-geode-voice-dock], [data-voice-confirm], [data-voice-next-step]';

/**
 * Les racines du périmètre : le contenu principal (`main`) et le tiroir / dialogue ouvert (visibles, hors zones exclues).
 * Écart à la spec constaté à l'écran : la plupart des pages des portails n'ont PAS de `<main>` (chaque page compose `HeaderPage`, son contenu et `Footer`) ;
 * à défaut de `main`, le contenu est le reste de la page autour de l'en-tête repéré par `data-voice-header` (l'en-tête et le pied restent exclus).
 */
export const scopeRoots = (doc: Document): HTMLElement[] => {
  const cache: VisibilityCache = new WeakMap();
  const visible = (root: HTMLElement): boolean => !root.closest(EXCLUDED_SELECTOR) && isRendered(root, cache);
  const mains = Array.from(doc.querySelectorAll<HTMLElement>('main, [role="main"]')).filter(visible);
  const pages =
    mains.length > 0
      ? mains
      : Array.from(doc.querySelectorAll<HTMLElement>('[data-voice-header]'))
          .map((header) => header.parentElement)
          .filter((page): page is HTMLElement => page !== null && !mains.includes(page) && isRendered(page, cache));
  const drawers = Array.from(doc.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"], dialog[open], [data-voice-scope]')).filter(visible);
  return [...pages, ...drawers.filter((drawer) => !pages.includes(drawer))];
};

/** Vrai quand l'élément est dans le périmètre (sous une racine, hors zone exclue). */
export const isInScope = (el: Element, roots: readonly HTMLElement[]): boolean =>
  roots.some((root) => root.contains(el)) && !el.closest(EXCLUDED_SELECTOR);

export interface UiCandidate {
  element: HTMLElement;
  role: UiRole;
  name: string;
  /** Le texte visible quand le nom vient d'un `aria-label` (« Open 1 » sur une carte qui montre « Prêt PR-041 Nora ») : une seconde chance de correspondre. */
  alt?: string;
}

const CANDIDATE_SELECTOR =
  'button, a[href], input, select, textarea, summary, tr, h1, h2, h3, h4, h5, h6, dialog, form, section, fieldset, table, ul, ol, [role], [contenteditable="true"], [tabindex="0"], [data-voice-clickable]';

/** Tous les éléments nommés du périmètre (visibles, hors sensibles), avec leur rôle et leur nom accessible. */
export const collectCandidates = (doc: Document, roots: readonly HTMLElement[] = scopeRoots(doc)): UiCandidate[] => {
  const cache: VisibilityCache = new WeakMap();
  const seen = new Set<Element>();
  const out: UiCandidate[] = [];
  roots.forEach((root) => {
    const elements: Element[] = [root, ...Array.from(root.querySelectorAll(CANDIDATE_SELECTOR))];
    elements.forEach((el) => {
      if (seen.has(el) || !(el instanceof (doc.defaultView?.HTMLElement ?? HTMLElement))) return;
      seen.add(el);
      if (el.closest(EXCLUDED_SELECTOR) || !isRendered(el, cache)) return;
      const role = roleOf(el);
      if (!role) return;
      if (role === 'link') {
        // Un lien vers un autre site n'est pas un geste de l'écran de Géode.
        const anchor = el as HTMLAnchorElement;
        const href = anchor.getAttribute('href') ?? '';
        if (/^[a-z][a-z0-9+.-]*:/i.test(href) && !href.startsWith(`${doc.location.protocol}//${doc.location.host}`)) return;
      }
      const name = accessibleName(el);
      if (!name) return;
      const labelled = attr(el, 'aria-label').trim() !== '' || attr(el, 'aria-labelledby').trim() !== '';
      const visible = labelled && (role === 'button' || role === 'link' || role === 'other') ? collapse(textOf(el)) : '';
      out.push({ element: el as HTMLElement, role, name, alt: visible && visible !== name ? (visible.length > 240 ? visible.slice(0, 240) : visible) : undefined });
    });
  });
  return out;
};

const STOP_WORDS = new Set(['le', 'la', 'les', 'l', 'un', 'une', 'de', 'du', 'des', 'd', 'the', 'a', 'an', 'of', 'to', 'der', 'die', 'das', 'il', 'lo', 'di', 'del', 'ال']);

const containsSequence = (haystack: readonly string[], needle: readonly string[]): boolean => {
  if (needle.length === 0) return false;
  for (let start = 0; start + needle.length <= haystack.length; start += 1) {
    if (needle.every((word, offset) => haystack[start + offset] === word)) return true;
  }
  return false;
};

/**
 * Force de la correspondance d'un nom demandé avec un nom accessible : 4 identique ; 3 les mots du nom demandé (sans mots vides) forment une suite de
 * MOTS ENTIERS du nom (« Livrer » dans « Ouvrir · Livrer », pas dans « Livraison ») ; 2 le texte est contenu ; 1 tous les mots comme préfixes ; 0 aucune.
 */
export const matchStrength = (query: string, name: string): 0 | 1 | 2 | 3 | 4 => {
  const q = normalizeUiText(query);
  const n = normalizeUiText(name);
  if (!q || !n) return 0;
  if (n === q) return 4;
  const nameWords = wordsOf(n);
  const meaningfulName = nameWords.filter((word) => !STOP_WORDS.has(word));
  const all = wordsOf(q);
  const meaningful = all.filter((word) => !STOP_WORDS.has(word));
  const tokens = meaningful.length > 0 ? meaningful : all;
  if (containsSequence(meaningfulName.length > 0 ? meaningfulName : nameWords, tokens)) return 3;
  if (q.length >= 3 && n.includes(q)) return 2;
  const phrase = meaningful.join(' ');
  if (phrase.length >= 3 && meaningfulName.join(' ').includes(phrase)) return 2;
  if (tokens.length > 0 && tokens.every((token) => nameWords.some((word) => word.startsWith(token)))) return 1;
  return 0;
};

const CONTAINER_ROLES = new Set<UiRole>(['row', 'region', 'group', 'form', 'dialog', 'table', 'list', 'tabpanel', 'listbox', 'radiogroup']);

export type UiResolution =
  | { kind: 'found'; candidate: UiCandidate }
  | { kind: 'ambiguous'; candidates: UiCandidate[] }
  | { kind: 'none' };

/**
 * Résout un nom parmi des candidats : la force la plus haute gagne ; un conteneur (ligne, région…) qui contient un autre candidat de la même force
 * s'efface devant lui ; plus d'un candidat restant = AMBIGUÏTÉ (aucune action, la liste est rendue).
 */
export const resolveCandidate = (candidates: readonly UiCandidate[], query: string): UiResolution => {
  let best = 0;
  const scored = candidates.map((candidate) => {
    const strength = Math.max(matchStrength(query, candidate.name), candidate.alt ? matchStrength(query, candidate.alt) : 0);
    best = Math.max(best, strength);
    return { candidate, strength };
  });
  if (best === 0) return { kind: 'none' };
  const top = scored.filter((entry) => entry.strength === best).map((entry) => entry.candidate);
  const specific = top.filter(
    (candidate) => !(CONTAINER_ROLES.has(candidate.role) && top.some((other) => other !== candidate && candidate.element.contains(other.element)))
  );
  const final = specific.length > 0 ? specific : top;
  return final.length === 1 ? { kind: 'found', candidate: final[0] } : { kind: 'ambiguous', candidates: final };
};

/** Résout un nom parmi les éléments du périmètre dont le rôle est dans `roles`. */
export const resolveByName = (doc: Document, query: string, roles: readonly UiRole[], roots: readonly HTMLElement[] = scopeRoots(doc)): UiResolution =>
  resolveCandidate(
    collectCandidates(doc, roots).filter((candidate) => roles.includes(candidate.role)),
    query
  );

const CONTEXT_SELECTOR = 'tr, [role="row"], li, [role="listitem"], article, [role="article"], fieldset, section, [role="region"], [role="group"], [role="dialog"]';

/** Un mot de contexte pour départager des candidats de même nom : la ligne, la carte ou la région qui les porte (≤ 60 caractères). */
export const contextOf = (el: Element, ownName = ''): string => {
  const holder = el.parentElement?.closest(CONTEXT_SELECTOR);
  if (!holder) return '';
  const label = collapse(attr(holder, 'aria-label'));
  let text = label || accessibleName(holder) || collapse(textOf(holder));
  if (ownName) {
    text = collapse(text.split(ownName).join(' '));
  }
  return text.length > 60 ? `${text.slice(0, 59)}…` : text;
};

/** Un élément est désactivé (attribut, `aria-disabled`, ou fieldset désactivé). */
export const isDisabled = (el: Element): boolean => {
  const control = el as HTMLButtonElement;
  return control.disabled === true || attr(el, 'aria-disabled') === 'true' || el.closest('fieldset[disabled]') !== null;
};

export const isReadOnly = (el: Element): boolean => (el as HTMLInputElement).readOnly === true || attr(el, 'aria-readonly') === 'true';

/** Dans une `Modal` de variante `destructive` (le fantôme ne la traverse pas : tout y est interdit à la voix). */
export const inDestructiveModal = (el: Element): boolean => el.closest('.ds-modal--destructive') !== null;

/** Le formulaire (ou le dialogue, ou la racine) qui porte l'élément : l'unité de « formulaire modifié par Géode ». */
export const formContainerOf = (el: Element): Element => el.closest('form, [role="dialog"], [role="alertdialog"], dialog') ?? el.closest('main, [role="main"]') ?? el.ownerDocument.body;

/** Le bouton soumet un formulaire : `type=submit`, ou bouton sans type dans un `<form>`. */
export const submitsForm = (el: Element): boolean => {
  const tag = el.tagName.toLowerCase();
  if (tag === 'input') {
    return (attr(el, 'type') || '').toLowerCase() === 'submit';
  }
  if (tag !== 'button') return false;
  const type = (attr(el, 'type') || 'submit').toLowerCase();
  return type === 'submit' && ((el as HTMLButtonElement).form !== null || el.closest('form') !== null);
};

const windowOf = (el: Element): Window & typeof globalThis => (el.ownerDocument.defaultView ?? window) as Window & typeof globalThis;

const mouse = (win: Window & typeof globalThis, type: string): Event => new win.MouseEvent(type, { bubbles: true, cancelable: true, composed: true, button: 0, view: win });

/** Joue un clic par les événements natifs du navigateur : pointeur (quand il existe), souris, `click`. */
export const clickElement = (el: HTMLElement): void => {
  const win = windowOf(el);
  el.focus?.({ preventScroll: true });
  if (typeof win.PointerEvent === 'function') {
    el.dispatchEvent(new win.PointerEvent('pointerdown', { bubbles: true, cancelable: true, composed: true, button: 0, pointerType: 'mouse', isPrimary: true }));
  }
  el.dispatchEvent(mouse(win, 'mousedown'));
  if (typeof win.PointerEvent === 'function') {
    el.dispatchEvent(new win.PointerEvent('pointerup', { bubbles: true, cancelable: true, composed: true, button: 0, pointerType: 'mouse', isPrimary: true }));
  }
  el.dispatchEvent(mouse(win, 'mouseup'));
  el.click();
};

/** Écrit une valeur par le SETTER NATIF du prototype (React suit la valeur par un traceur posé sur l'élément : l'écriture directe lui échappe). */
export const setNativeValue = (el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, value: string): void => {
  const win = windowOf(el);
  const tag = el.tagName.toLowerCase();
  const proto = tag === 'select' ? win.HTMLSelectElement.prototype : tag === 'textarea' ? win.HTMLTextAreaElement.prototype : win.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
};

const dispatchInput = (el: HTMLElement, data: string): void => {
  const win = windowOf(el);
  const event =
    typeof win.InputEvent === 'function'
      ? new win.InputEvent('input', { bubbles: true, cancelable: false, composed: true, inputType: 'insertReplacementText', data })
      : new win.Event('input', { bubbles: true });
  el.dispatchEvent(event);
};

const dispatchChange = (el: HTMLElement): void => {
  el.dispatchEvent(new (windowOf(el).Event)('change', { bubbles: true }));
};

const MONTHS: Readonly<Record<string, number>> = {
  janvier: 1, fevrier: 2, mars: 3, avril: 4, mai: 5, juin: 6, juillet: 7, aout: 8, septembre: 9, octobre: 10, novembre: 11, decembre: 12,
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6, july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  januar: 1, februar: 2, marz: 3, juni: 6, juli: 7, oktober: 10, dezember: 12,
  gennaio: 1, febbraio: 2, marzo: 3, aprile: 4, maggio: 5, giugno: 6, luglio: 7, agosto: 8, settembre: 9, ottobre: 10, dicembre: 12,
};

const pad = (value: number): string => String(value).padStart(2, '0');

const isoDate = (year: number, month: number, day: number): string | null => {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? `${year}-${pad(month)}-${pad(day)}` : null;
};

/**
 * Une date dite ou écrite, en `AAAA-MM-JJ` (valeur d'un `input[type=date]`) : ISO, `24.10.2026`, `24/10/2026`, `24 octobre 2026`, `24 octobre`
 * (l'année courante). `null` quand elle n'est pas lisible.
 */
export const parseDateValue = (value: string, now: Date = new Date()): string | null => {
  const text = normalizeUiText(value).replace(/^(?:le|the|am|il|el)\s+/, '').replace(/(\d)(?:er|st|nd|rd|th)\b/, '$1');
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (iso) return isoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const numeric = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(text);
  if (numeric) return isoDate(Number(numeric[3]), Number(numeric[2]), Number(numeric[1]));
  const named = /^(\d{1,2})\s+([a-z]+)\.?(?:\s+(\d{4}))?$/.exec(text);
  if (named && MONTHS[named[2]]) {
    return isoDate(named[3] ? Number(named[3]) : now.getFullYear(), MONTHS[named[2]], Number(named[1]));
  }
  return null;
};

/** Un nombre dit ou écrit (« 12 000 », « 12'000,50 », « 1.5 ») en valeur de `input[type=number]` ; `null` quand il n'est pas lisible. */
export const parseNumberValue = (value: string): string | null => {
  const text = value.replace(/[\s\u00a0\u202f']/g, '').replace(/,/g, '.');
  const dots = text.split('.').length - 1;
  const cleaned = dots > 1 ? text.replace(/\./g, '') : text;
  return /^-?\d+(\.\d+)?$/.test(cleaned) ? cleaned : null;
};

/** Une heure `HH:MM` (« 14h30 », « 14:30 »). */
export const parseTimeValue = (value: string): string | null => {
  const match = /^(\d{1,2})\s*(?:h|:)\s*(\d{2})?$/.exec(normalizeUiText(value));
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  return hours < 24 && minutes < 60 ? `${pad(hours)}:${pad(minutes)}` : null;
};

/** Une valeur d'argument d'outil en texte : chaîne, nombre ou booléen ; tout le reste (objet, absent) est vide. */
export const valueToText = (value: unknown): string =>
  typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';

const TRUE_WORDS = new Set(['true', 'oui', 'yes', 'on', '1', 'coche', 'cochee', 'checked', 'ja', 'si', 'نعم', 'vrai', 'actif', 'active']);
const FALSE_WORDS = new Set(['false', 'non', 'no', 'off', '0', 'decoche', 'decochee', 'unchecked', 'nein', 'لا', 'faux', 'inactif']);

/** Une valeur dite en booléen (case à cocher) ; `null` quand elle n'est ni oui ni non. */
export const parseBooleanValue = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  const text = normalizeUiText(valueToText(value));
  if (TRUE_WORDS.has(text)) return true;
  if (FALSE_WORDS.has(text)) return false;
  return null;
};

export type UiFillResult =
  | { ok: true; kind: 'text' | 'check'; previous: string | boolean; shown: string }
  | { ok: false; error: 'invalid_value' | 'read_only' | 'disabled' | 'sensitive' | 'unsupported' };

/** Ce que le champ montre : la valeur (listes : le texte de l'option choisie), `true` / `false` pour une case. */
export const readFieldValue = (el: Element): string | boolean => {
  const tag = el.tagName.toLowerCase();
  if (tag === 'select') {
    const select = el as HTMLSelectElement;
    const options = Array.from(select.selectedOptions ?? []);
    return options.map((option) => collapse(option.textContent ?? '')).join(', ');
  }
  if (tag === 'input') {
    const input = el as HTMLInputElement;
    const type = (attr(el, 'type') || 'text').toLowerCase();
    if (type === 'checkbox' || type === 'radio') return input.checked;
    return input.value;
  }
  if (tag === 'textarea') return (el as HTMLTextAreaElement).value;
  const role = roleOf(el);
  if (role === 'checkbox' || role === 'switch' || role === 'radio') return attr(el, 'aria-checked') === 'true';
  return collapse(el.textContent ?? '');
};

/**
 * Remplit un champ par le jeu natif des événements. Texte, nombre, date, heure, zone de texte : valeur normalisée selon le type, `focus`, setter
 * natif, `input` + `change`, `blur`. Case à cocher / interrupteur : `click` seulement quand l'état diffère. Une valeur que le champ ne retient pas
 * (type date illisible…) est refusée et rien ne bouge.
 */
export const fillElement = (el: HTMLElement, rawValue: unknown): UiFillResult => {
  if (isSensitive(el)) return { ok: false, error: 'sensitive' };
  if (isDisabled(el)) return { ok: false, error: 'disabled' };
  const role = roleOf(el);
  if (role === 'checkbox' || role === 'switch') {
    const wanted = parseBooleanValue(rawValue);
    if (wanted === null) return { ok: false, error: 'invalid_value' };
    const current = readFieldValue(el) === true;
    if (current !== wanted) clickElement(el);
    return { ok: true, kind: 'check', previous: current, shown: wanted ? 'true' : 'false' };
  }
  const tag = el.tagName.toLowerCase();
  if (tag === 'select') {
    return { ok: false, error: 'unsupported' };
  }
  if (tag !== 'input' && tag !== 'textarea') {
    return { ok: false, error: 'unsupported' };
  }
  if (isReadOnly(el)) return { ok: false, error: 'read_only' };
  const input = el as HTMLInputElement | HTMLTextAreaElement;
  const type = tag === 'input' ? (attr(el, 'type') || 'text').toLowerCase() : 'textarea';
  let value = valueToText(rawValue);
  if (type === 'date') {
    const parsed = parseDateValue(value);
    if (!parsed) return { ok: false, error: 'invalid_value' };
    value = parsed;
  } else if (type === 'number') {
    const parsed = parseNumberValue(value);
    if (!parsed) return { ok: false, error: 'invalid_value' };
    value = parsed;
  } else if (type === 'time') {
    const parsed = parseTimeValue(value);
    if (!parsed) return { ok: false, error: 'invalid_value' };
    value = parsed;
  } else if (type === 'checkbox' || type === 'radio' || type === 'file' || type === 'range' || type === 'color') {
    return { ok: false, error: 'unsupported' };
  }
  const previous = input.value;
  input.focus?.({ preventScroll: true });
  setNativeValue(input, value);
  if (input.value !== value) {
    // Le champ a refusé la valeur (type, longueur, motif) : rien ne bouge.
    setNativeValue(input, previous);
    return { ok: false, error: 'invalid_value' };
  }
  dispatchInput(input, value);
  dispatchChange(input);
  input.blur?.();
  return { ok: true, kind: 'text', previous, shown: value };
};

export interface UiOption {
  element: HTMLElement | null;
  value: string;
  label: string;
}

/** Les options d'une liste native ou d'un groupe de boutons radio. */
export const optionsOf = (el: HTMLElement): UiOption[] => {
  const tag = el.tagName.toLowerCase();
  if (tag === 'select') {
    return Array.from((el as HTMLSelectElement).options)
      .filter((option) => !option.disabled)
      .map((option) => ({ element: option, value: option.value, label: collapse(option.textContent ?? '') }));
  }
  const cache: VisibilityCache = new WeakMap();
  return Array.from(el.querySelectorAll<HTMLElement>('input[type="radio"], [role="radio"], [role="option"]'))
    .filter((option) => isRendered(option, cache) && !isDisabled(option))
    .map((option) => ({ element: option, value: attr(option, 'value'), label: accessibleName(option) }));
};

/** Choisit l'option dont le texte (ou la valeur) correspond le mieux ; ambiguïté = `ambiguous` (aucune action). */
export const matchOption = (options: readonly UiOption[], query: string): { kind: 'found'; option: UiOption } | { kind: 'ambiguous'; options: UiOption[] } | { kind: 'none' } => {
  let best = 0;
  const scored = options.map((option) => {
    const strength = Math.max(matchStrength(query, option.label), matchStrength(query, option.value));
    best = Math.max(best, strength);
    return { option, strength };
  });
  if (best === 0) return { kind: 'none' };
  const top = scored.filter((entry) => entry.strength === best).map((entry) => entry.option);
  return top.length === 1 ? { kind: 'found', option: top[0] } : { kind: 'ambiguous', options: top };
};

/** Choisit une option d'une liste native (`change`) ; un groupe radio / ARIA est cliqué par l'appelant. */
export const selectNativeOption = (el: HTMLSelectElement, option: UiOption): { previous: string } => {
  const previous = el.value;
  el.focus?.({ preventScroll: true });
  setNativeValue(el, option.value);
  dispatchInput(el, option.value);
  dispatchChange(el);
  el.blur?.();
  return { previous };
};

/** Restaure une valeur (annulation) : texte / liste par le setter natif, case par `click` quand l'état diffère. */
export const restoreFieldValue = (el: HTMLElement, previous: string | boolean): boolean => {
  if (!el.isConnected) return false;
  if (typeof previous === 'boolean') {
    if (readFieldValue(el) !== previous) clickElement(el);
    return true;
  }
  const tag = el.tagName.toLowerCase();
  if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return false;
  const control = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
  control.focus?.({ preventScroll: true });
  setNativeValue(control, previous);
  dispatchInput(control, previous);
  dispatchChange(control);
  control.blur?.();
  return true;
};

/** Fait défiler l'élément au centre de la vue (jsdom n'a pas `scrollIntoView`). */
export const scrollElementIntoView = (el: HTMLElement): void => {
  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'center', inline: 'nearest' });
  }
};

/** Le conteneur qui défile le contenu principal (le premier ancêtre défilant, sinon la page). */
export const findScroller = (doc: Document): HTMLElement => {
  const view = doc.defaultView;
  const main = doc.querySelector<HTMLElement>('main, [role="main"]');
  let node: HTMLElement | null = main;
  while (node && node !== doc.documentElement) {
    const overflow = view?.getComputedStyle(node).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return (doc.scrollingElement as HTMLElement | null) ?? doc.documentElement;
};

/** Fait défiler `scroller` à `top` (ou d'une portion) ; `down` / `up` = 80 % de la hauteur visible. */
export const scrollTo = (doc: Document, to: 'top' | 'bottom' | 'up' | 'down'): void => {
  const scroller = findScroller(doc);
  const view = doc.defaultView;
  const page = Math.round((scroller === doc.scrollingElement || scroller === doc.documentElement ? view?.innerHeight ?? 800 : scroller.clientHeight) * 0.8);
  const target =
    to === 'top' ? 0 : to === 'bottom' ? scroller.scrollHeight : Math.max(0, scroller.scrollTop + (to === 'down' ? page : -page));
  if (typeof scroller.scrollTo === 'function') {
    scroller.scrollTo({ top: target });
  } else {
    scroller.scrollTop = target;
  }
};
